import {
  EDGELESS_GRID_STYLE_ATTRIBUTE,
  EDGELESS_GRID_STYLE_CHANGED_EVENT,
  normalizeEdgelessGridStyle,
} from '@blocksuite/affine-block-surface';
import {
  edgelessToolbarI18nT,
  EditorSettingProvider,
} from '@blocksuite/affine-shared/services';
import {
  MAXIMAP_RADIUS,
  minimapCanvasHeight,
  minimapCanvasHeightLarge,
  minimapCanvasHeightMedium,
  minimapPanelBackground,
  minimapPanelBorder,
  minimapPanelBorderRadius,
  minimapPanelBoxSizing,
  minimapPanelShadow,
} from '@blocksuite/affine-shared/styles';
import {
  EdgelessWheelGestureRouter,
  edgelessWheelZoomDeltaY,
  stepRemotePresenceCursorPosition,
  streamLainAnimationController,
} from '@blocksuite/affine-shared/utils';
import { Bound, getCommonBoundWithRotation } from '@blocksuite/global/gfx';
import { WithDisposable } from '@blocksuite/global/lit';
import type { BlockStdScope } from '@blocksuite/std';
import {
  GfxControllerIdentifier,
  type GfxModel,
  type GfxNavigationHost,
  zoomFromWheel,
} from '@blocksuite/std/gfx';
import { isHiddenSystemManagedDatabase } from '@streamlain/database-identity';
import { css, html, LitElement, nothing, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';

import { observeMaximapLayout } from './maximap-layout.js';
import {
  handOverToSmallMap,
  type MapCamera,
  MAXIMAP_OPEN_ATTRIBUTE,
  type MaximapEnd,
  type MaximapMorphFrame,
  maximapMorphFrame,
} from './maximap-morph.js';
import { maximapStyles } from './maximap-styles.js';
import { minimapWorldBound, paintMinimapWorld } from './minimap-world.js';

// Default color for remote cursors when user has no color set
const DEFAULT_CURSOR_COLOR = '#6b7280';

type MinimapCursorPosition = {
  x: number;
  y: number;
};

type MinimapElement = GfxModel;

/** A retained projection can use the same map without mounting an editor. */
export type MinimapHost = GfxNavigationHost;

/** What one paint shows: the box it fills from the canvas's top left and the world placed in it. */
interface MinimapView {
  width: number;
  height: number;
  bound: Bound;
  scale: number;
  offsetX: number;
  offsetY: number;
  /** 0 = the small map's plain picture, 1 = the overview with sectors, coordinates and names. */
  detail: number;
}

/**
 * The map shows where content lies, not what it is: every element in one quiet
 * grey of the theme, frames as an outline. The brand blue is kept for the view,
 * the one thing the map says "you are here" with; people keep their colours.
 */
const MINIMAP_TOKENS = {
  content: '--streamlain-v2-icon-tertiary',
  frame: '--streamlain-v2-icon-disable',
  view: '--streamlain-v2-brand-primary',
} as const;
type MinimapPalette = Record<keyof typeof MINIMAP_TOKENS, string>;
const VIEW_FILL_ALPHA = 0.12;

export class EdgelessMinimapCanvas extends WithDisposable(LitElement) {
  static override styles = [
    css`
      :host {
        display: block;
        width: 100%;
        height: ${unsafeCSS(minimapCanvasHeight)};
        min-height: ${unsafeCSS(minimapCanvasHeight)};
        max-height: ${unsafeCSS(minimapCanvasHeight)};
        box-sizing: ${unsafeCSS(minimapPanelBoxSizing)};
        border-radius: ${unsafeCSS(minimapPanelBorderRadius)};
        overflow: hidden;
        background: ${unsafeCSS(minimapPanelBackground)};
        box-shadow: ${unsafeCSS(minimapPanelShadow)};
        border: ${unsafeCSS(minimapPanelBorder)};
      }

      /* StreamLain: Responsive minimap heights */
      @container viewport (width > 1024px) and (width <= 1440px) {
        :host {
          height: ${unsafeCSS(minimapCanvasHeightMedium)};
          min-height: ${unsafeCSS(minimapCanvasHeightMedium)};
          max-height: ${unsafeCSS(minimapCanvasHeightMedium)};
        }
      }

      @container viewport (width > 1440px) {
        :host {
          height: ${unsafeCSS(minimapCanvasHeightLarge)};
          min-height: ${unsafeCSS(minimapCanvasHeightLarge)};
          max-height: ${unsafeCSS(minimapCanvasHeightLarge)};
        }
      }

      canvas {
        display: block;
        width: 100%;
        height: 100%;
        cursor: pointer;
      }

      canvas.dragging {
        cursor: grabbing;
      }
    `,
    maximapStyles,
  ];

  private _width = 180;
  private _height = 120;
  private _dpr = 0;
  private _scale = 1;
  private _offsetX = 0;
  private _offsetY = 0;
  private _detail = 0;
  private _fullBound: Bound | null = null;
  /** The step of the overview's growth this map paints instead of its rest view. */
  private _morphFrame: MaximapMorphFrame | null = null;
  /** How far the overview has grown out of this small map: 0 = closed, 1 = at rest. */
  private _morphProgress = 0;
  private _renderFrame: number | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _removeWindowResizeListener: (() => void) | null = null;
  private _dragging = false;
  private _lastPointerPos: [number, number] | null = null;
  private readonly _remoteCursorTargets = new Map<
    number,
    MinimapCursorPosition
  >();
  private readonly _displayedRemoteCursors = new Map<
    number,
    MinimapCursorPosition
  >();
  private _remoteCursorLerpFrame: number | null = null;
  private _remoteCursorLastLerpTimestamp: number | null = null;
  private readonly _wheelRouter = new EdgelessWheelGestureRouter();
  private _outsidePointerDown = false;
  private _stopMaximapLayout: (() => void) | null = null;

  get gfx() {
    return this.host?.gfx ?? this.std.get(GfxControllerIdentifier);
  }

  private get store() {
    return this.host?.store ?? this.std.store;
  }

  @property({ attribute: false })
  accessor host: MinimapHost | undefined;

  get viewport() {
    return this.gfx.viewport;
  }

  @query('canvas') private accessor _canvas!: HTMLCanvasElement;

  @query('dialog') private accessor _dialog!: Element | null;

  @query('.maximap-surface')
  private accessor _maximapSurface!: Element | null;

  @query('.maximap-frame')
  private accessor _maximapFrame!: HTMLElement | null;

  @query('.maximap-frame > edgeless-minimap-canvas')
  private accessor _maximap!: EdgelessMinimapCanvas | null;

  @property({ attribute: false })
  accessor std!: BlockStdScope;

  @property({ type: Boolean, reflect: true })
  accessor expanded = false;

  @state()
  private accessor _maximapOpen = false;

  override connectedCallback() {
    super.connectedCallback();

    this.addEventListener('open-maximap', this._handleOpenMaximap);
    this.addEventListener('toggle-maximap', this._handleToggleMaximap);
    window.addEventListener(
      EDGELESS_GRID_STYLE_CHANGED_EVENT,
      this._handleGridStyleChanged
    );

    // Subscribe to viewport changes
    this.disposables.add(
      this.viewport.viewportUpdated.subscribe(() => {
        this._scheduleRender();
      })
    );

    // Subscribe to element changes
    const surface = this.gfx.surface;
    if (surface) {
      this.disposables.add(
        surface.elementAdded.subscribe(() => this._scheduleRender())
      );
      this.disposables.add(
        surface.elementRemoved.subscribe(() => this._scheduleRender())
      );
      this.disposables.add(
        surface.elementUpdated.subscribe(() => this._scheduleRender())
      );
    }

    // Subscribe to block changes
    this.disposables.add(
      this.store.slots.blockUpdated.subscribe(() => this._scheduleRender())
    );
    if (this.gfx.selection) {
      this.disposables.add(
        this.gfx.selection.slots.transientGeometryUpdated.subscribe(() =>
          this._scheduleRender()
        )
      );

      // Subscribe to remote cursor changes for presence indicators
      this.disposables.add(
        this.gfx.selection.slots.remoteCursorUpdated.subscribe(() =>
          this._updateRemoteCursorTargets()
        )
      );
    }

    this._updateRemoteCursorTargets();
  }

  override firstUpdated() {
    this._startCanvasResizeSync();
    this._syncCanvasSizeFromElement();
    this._scheduleRender();
  }

  override disconnectedCallback() {
    this.removeEventListener('open-maximap', this._handleOpenMaximap);
    this.removeEventListener('toggle-maximap', this._handleToggleMaximap);
    window.removeEventListener(
      EDGELESS_GRID_STYLE_CHANGED_EVENT,
      this._handleGridStyleChanged
    );
    this._stopMaximapLayout?.();
    this._stopMaximapLayout = null;
    if (this._maximapFrame) {
      streamLainAnimationController.cancel(this._maximapFrame);
    }
    this._finishMaximapClose();
    this._maximapOpen = false;
    if (this._renderFrame !== null) cancelAnimationFrame(this._renderFrame);
    this._renderFrame = null;
    this._stopRemoteCursorLerp();
    this._stopCanvasResizeSync();
    super.disconnectedCallback();
  }

  private readonly _handleGridStyleChanged = () => this._scheduleRender();

  private _scheduleRender() {
    if (!this.isConnected || this._maximapOpen || this._renderFrame !== null)
      return;
    this._renderFrame = requestAnimationFrame(() => {
      this._renderFrame = null;
      if (!this.isConnected || this._maximapOpen) return;
      this._render();
    });
  }

  private _render() {
    if (!this._canvas) return;

    const ctx = this._canvas.getContext('2d');
    if (!ctx) return;

    if (this._width <= 0 || this._height <= 0) return;

    this._syncCanvasResolution(ctx);

    // Clear
    ctx.clearRect(0, 0, this._width, this._height);

    const elements = this._getVisibleViewportBoundElements();
    const view = this._morphFrame
      ? this._morphView(this._morphFrame, elements)
      : this._restView(elements);
    this._fullBound = view.bound;
    this._scale = view.scale;
    this._offsetX = view.offsetX;
    this._offsetY = view.offsetY;
    this._detail = view.detail;

    if (view.detail > 0) {
      const color = getComputedStyle(this).color || '#888';
      paintMinimapWorld(
        ctx,
        this._fullBound,
        this._scale,
        this._offsetX,
        this._offsetY,
        color,
        view.detail,
        this.store.canvasWorld,
        normalizeEdgelessGridStyle(
          document.documentElement.getAttribute(EDGELESS_GRID_STYLE_ATTRIBUTE)
        ) === 'lines'
      );
    }
    ctx.save();
    if (this.expanded) {
      ctx.beginPath();
      ctx.rect(
        this._offsetX,
        this._offsetY,
        this._fullBound.w * this._scale,
        this._fullBound.h * this._scale
      );
      ctx.clip();
    }

    // Both overview sizes use the same content, viewport and presence renderer.
    const palette = this._palette();
    this._renderElements(ctx, elements, palette);

    // Render viewport
    this._renderViewportIndicator(ctx, palette);

    // Render remote cursors (presence indicators)
    this._renderRemoteCursors(ctx);
    ctx.restore();
  }

  private _contentBound(elements: MinimapElement[]) {
    return elements.length > 0
      ? getCommonBoundWithRotation(
          elements.map(element => element.elementBound)
        )
      : null;
  }

  /** The map at rest: its world fitted into the whole canvas. */
  private _restView(elements: MinimapElement[]): MinimapView {
    const contentBound = this._contentBound(elements);
    const padding = this.expanded ? 18 : 8;
    const innerWidth = Math.max(1, this._width - padding * 2);
    const innerHeight = Math.max(1, this._height - padding * 2);
    const bound = minimapWorldBound(
      contentBound,
      this.viewport.viewportBounds,
      this.expanded,
      innerWidth / innerHeight,
      this.store.canvasWorld
    );
    const scale = Math.min(innerWidth / bound.w, innerHeight / bound.h);
    return {
      width: this._width,
      height: this._height,
      bound,
      scale,
      offsetX: (this._width - bound.w * scale) / 2,
      offsetY: (this._height - bound.h * scale) / 2,
      detail: this.expanded ? 1 : 0,
    };
  }

  /** The overview on its way: the step's camera in the step's box. */
  private _morphView(
    { box, camera, detail }: MaximapMorphFrame,
    elements: MinimapElement[]
  ): MinimapView {
    // A bounded world grows into the world; an unbounded one into its content.
    const world = this.store.canvasWorld;
    const bound = minimapWorldBound(
      world === 'bounded' ? null : this._contentBound(elements),
      this.viewport.viewportBounds,
      this.expanded,
      1,
      world
    );
    return {
      width: box.width,
      height: box.height,
      bound,
      scale: camera.scale,
      offsetX: box.width / 2 - (camera.centerX - bound.x) * camera.scale,
      offsetY: box.height / 2 - (camera.centerY - bound.y) * camera.scale,
      detail,
    };
  }

  /** What the map shows at rest, measured now; null while it has no size. */
  restCamera(): MapCamera | null {
    this._syncCanvasSizeFromElement();
    if (!this._canvas || this._width <= 0 || this._height <= 0) return null;
    const view = this._restView(this._getVisibleViewportBoundElements());
    return {
      scale: view.scale,
      centerX: view.bound.x + (view.width / 2 - view.offsetX) / view.scale,
      centerY: view.bound.y + (view.height / 2 - view.offsetY) / view.scale,
    };
  }

  /** Paint a step of the overview's growth now; null returns the map to its rest view. */
  paintMorph(frame: MaximapMorphFrame | null) {
    this._morphFrame = frame;
    this._render();
  }

  private _startCanvasResizeSync() {
    if (!this._canvas) return;

    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(entries => {
        const size = this._getResizeObserverSize(entries[0]);
        if (size) {
          this._applyCanvasCssSize(size.width, size.height);
          return;
        }
        this._syncCanvasSizeFromElement();
      });
      this._resizeObserver.observe(this._canvas);
      return;
    }

    const onResize = () => {
      this._syncCanvasSizeFromElement();
    };
    window.addEventListener('resize', onResize);
    this._removeWindowResizeListener = () => {
      window.removeEventListener('resize', onResize);
    };
  }

  private _stopCanvasResizeSync() {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._removeWindowResizeListener?.();
    this._removeWindowResizeListener = null;
  }

  private _syncCanvasSizeFromElement() {
    if (!this._canvas) return;

    const rect = this._canvas.getBoundingClientRect();
    this._applyCanvasCssSize(rect.width, rect.height);
  }

  private _applyCanvasCssSize(width: number, height: number) {
    if (width <= 0 || height <= 0) return;
    if (width === this._width && height === this._height) return;

    this._width = width;
    this._height = height;
    this._scheduleRender();
  }

  private _getResizeObserverSize(
    entry: ResizeObserverEntry | undefined
  ): { width: number; height: number } | null {
    if (!entry) return null;

    const borderBoxSize = entry.borderBoxSize;
    const boxSize = Array.isArray(borderBoxSize)
      ? borderBoxSize[0]
      : borderBoxSize;
    if (boxSize) {
      if (boxSize.inlineSize <= 0 || boxSize.blockSize <= 0) return null;
      return {
        width: boxSize.inlineSize,
        height: boxSize.blockSize,
      };
    }

    const { width, height } = entry.contentRect;
    if (width <= 0 || height <= 0) return null;
    return { width, height };
  }

  private _syncCanvasResolution(ctx: CanvasRenderingContext2D) {
    const dpr = window.devicePixelRatio || 1;
    const pixelWidth = Math.max(1, Math.floor(this._width * dpr));
    const pixelHeight = Math.max(1, Math.floor(this._height * dpr));

    if (
      this._canvas.width !== pixelWidth ||
      this._canvas.height !== pixelHeight ||
      this._dpr !== dpr
    ) {
      this._canvas.width = pixelWidth;
      this._canvas.height = pixelHeight;
      this._dpr = dpr;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private _getVisibleViewportBoundElements() {
    return this.gfx.viewportBoundElements.filter(
      element => !this._isHiddenSystemManagedDatabaseElement(element)
    );
  }

  private _isHiddenSystemManagedDatabaseElement(element: MinimapElement) {
    if (!('flavour' in element) || element.flavour !== 'affine:database') {
      return false;
    }
    const databaseElement = element as {
      id: string;
      props: Record<string, unknown>;
    };
    return isHiddenSystemManagedDatabase({
      id: databaseElement.id,
      managedBy: databaseElement.props.managedBy,
      visible: databaseElement.props.visible,
    });
  }

  /** The theme's colours for one paint, read from the custom properties of the page. */
  private _palette(): MinimapPalette {
    const style = getComputedStyle(this);
    const read = (token: string) => {
      const value = style.getPropertyValue(token).trim();
      if (!value)
        throw new Error(`The minimap needs the theme colour ${token}`);
      return value;
    };
    return {
      content: read(MINIMAP_TOKENS.content),
      frame: read(MINIMAP_TOKENS.frame),
      view: read(MINIMAP_TOKENS.view),
    };
  }

  private _renderElements(
    ctx: CanvasRenderingContext2D,
    elements: MinimapElement[],
    palette: MinimapPalette
  ) {
    if (!this._fullBound) return;

    for (const el of elements) {
      const bound = el.elementBound;
      if (!bound) continue;

      const x = this._offsetX + (bound.x - this._fullBound.x) * this._scale;
      const y = this._offsetY + (bound.y - this._fullBound.y) * this._scale;
      // The overview keeps the smallest element visible.
      const w = Math.max(2 * this._detail, bound.w * this._scale);
      const h = Math.max(2 * this._detail, bound.h * this._scale);

      if (w < 1 || h < 1) continue;

      if ('flavour' in el && el.flavour === 'affine:frame') {
        ctx.strokeStyle = palette.frame;
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, w, h);
      } else {
        ctx.fillStyle = palette.content;
        ctx.fillRect(x, y, w, h);
      }
    }
  }

  private _renderViewportIndicator(
    ctx: CanvasRenderingContext2D,
    palette: MinimapPalette
  ) {
    if (!this._fullBound) return;

    const vp = this.viewport.viewportBounds;

    const x = this._offsetX + (vp.x - this._fullBound.x) * this._scale;
    const y = this._offsetY + (vp.y - this._fullBound.y) * this._scale;
    const w = vp.w * this._scale;
    const h = vp.h * this._scale;

    ctx.save();
    ctx.globalAlpha = VIEW_FILL_ALPHA;
    ctx.fillStyle = palette.view;
    ctx.fillRect(x, y, w, h);
    ctx.restore();

    ctx.strokeStyle = palette.view;
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w, h);
  }

  private _startRemoteCursorLerp() {
    if (
      !this.isConnected ||
      this._maximapOpen ||
      this._remoteCursorLerpFrame !== null
    )
      return;

    this._remoteCursorLerpFrame = requestAnimationFrame(
      this._lerpRemoteCursors
    );
  }

  private _stopRemoteCursorLerp() {
    if (this._remoteCursorLerpFrame === null) return;

    cancelAnimationFrame(this._remoteCursorLerpFrame);
    this._remoteCursorLerpFrame = null;
    this._remoteCursorLastLerpTimestamp = null;
  }

  private _updateRemoteCursorTargets() {
    const activeClientIds = new Set<number>();

    this.gfx.selection?.remoteCursorSelectionMap.forEach((cursor, clientId) => {
      activeClientIds.add(clientId);
      this._remoteCursorTargets.set(clientId, { x: cursor.x, y: cursor.y });

      if (!this._displayedRemoteCursors.has(clientId)) {
        this._displayedRemoteCursors.set(clientId, {
          x: cursor.x,
          y: cursor.y,
        });
      }
    });

    for (const clientId of this._remoteCursorTargets.keys()) {
      if (!activeClientIds.has(clientId)) {
        this._remoteCursorTargets.delete(clientId);
        this._displayedRemoteCursors.delete(clientId);
      }
    }

    this._startRemoteCursorLerp();
  }

  private readonly _lerpRemoteCursors = (timestamp: number) => {
    this._remoteCursorLerpFrame = null;

    const elapsed =
      this._remoteCursorLastLerpTimestamp === null
        ? 16
        : timestamp - this._remoteCursorLastLerpTimestamp;
    this._remoteCursorLastLerpTimestamp = timestamp;

    let hasMovingCursor = false;

    for (const [clientId, target] of this._remoteCursorTargets) {
      const displayed = this._displayedRemoteCursors.get(clientId);
      if (!displayed) continue;

      const stepped = stepRemotePresenceCursorPosition({
        current: displayed,
        elapsedMs: elapsed,
        target,
      });
      displayed.x = stepped.next.x;
      displayed.y = stepped.next.y;
      hasMovingCursor ||= stepped.moving;
    }

    this._render();

    if (hasMovingCursor) {
      this._startRemoteCursorLerp();
    } else {
      this._remoteCursorLastLerpTimestamp = null;
    }
  };

  /**
   * Render remote user cursors as colored circles.
   * Uses same data source as EdgelessRemoteSelectionWidget (DRY).
   */
  private _renderRemoteCursors(ctx: CanvasRenderingContext2D) {
    if (!this._fullBound) return;

    const awarenessStates = this.store.awarenessStore.getStates();

    this._displayedRemoteCursors.forEach((cursor, clientId) => {
      // Transform canvas coordinates to minimap coordinates
      const x = this._offsetX + (cursor.x - this._fullBound!.x) * this._scale;
      const y = this._offsetY + (cursor.y - this._fullBound!.y) * this._scale;

      // Get user color from awareness (same source as RemoteColorManager)
      const color =
        awarenessStates.get(clientId)?.color ?? DEFAULT_CURSOR_COLOR;

      // Draw filled circle with white border
      ctx.beginPath();
      ctx.arc(x, y, 5 + this._detail, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      const name = awarenessStates.get(clientId)?.user?.name;
      if (this._detail === 1 && name) {
        ctx.font = '12px system-ui, sans-serif';
        ctx.textBaseline = 'middle';
        const label = name.length > 24 ? name.slice(0, 23) + '…' : name;
        const width = ctx.measureText(label).width + 12;
        const labelX = Math.max(
          this._offsetX,
          Math.min(
            x + 10,
            this._offsetX + this._fullBound!.w * this._scale - width
          )
        );
        const labelY = Math.max(
          this._offsetY + 10,
          Math.min(y, this._offsetY + this._fullBound!.h * this._scale - 10)
        );
        ctx.fillStyle = '#16191ee6';
        ctx.fillRect(labelX, labelY - 10, width, 20);
        ctx.fillStyle = '#fff';
        ctx.fillText(label, labelX + 6, labelY);
      }
    });
  }

  private _navigateToPoint(e: PointerEvent) {
    if (!this._fullBound || this.viewport.locked) return;
    const rect = this._canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const modelX =
      this._fullBound.x +
      Math.max(
        0,
        Math.min(this._fullBound.w, (mx - this._offsetX) / this._scale)
      );
    const modelY =
      this._fullBound.y +
      Math.max(
        0,
        Math.min(this._fullBound.h, (my - this._offsetY) / this._scale)
      );

    this.viewport.setCenter(modelX, modelY);
  }

  private readonly _onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 || this.viewport.locked || !this._fullBound) return;
    e.preventDefault();
    e.stopPropagation();
    this._canvas.focus({ preventScroll: true });
    this._navigateToPoint(e);
    this._dragging = true;
    this._lastPointerPos = [e.clientX, e.clientY];
    this._canvas.setPointerCapture(e.pointerId);
    this._canvas.classList.add('dragging');
  };

  private readonly _onPointerMove = (e: PointerEvent) => {
    if (!this._dragging || !this._lastPointerPos) return;
    e.stopPropagation();

    const dx = (e.clientX - this._lastPointerPos[0]) / this._scale;
    const dy = (e.clientY - this._lastPointerPos[1]) / this._scale;

    this.viewport.applyDeltaCenter(dx, dy);
    this._lastPointerPos = [e.clientX, e.clientY];
  };

  private readonly _onPointerUp = (e: PointerEvent) => {
    this._dragging = false;
    this._lastPointerPos = null;
    if (this._canvas.hasPointerCapture(e.pointerId))
      this._canvas.releasePointerCapture(e.pointerId);
    this._canvas.classList.remove('dragging');
  };

  private readonly _onWheel = (event: WheelEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (this.viewport.locked) return;
    const scrollZoom =
      this.std?.getOptional(EditorSettingProvider)?.setting$.peek()
        .edgelessScrollZoom ?? true;
    if (this._wheelRouter.resolve(event, scrollZoom) === 'zoom') {
      this.viewport.setZoom(
        zoomFromWheel(this.viewport.zoom, edgelessWheelZoomDeltaY(event), {
          deltaMode: event.deltaMode,
          ctrlKey: event.ctrlKey,
          viewportHeight: this.viewport.height,
        }),
        undefined,
        true
      );
    } else {
      const horizontal = event.shiftKey && event.deltaX === 0;
      this.viewport.applyDeltaCenter(
        (horizontal ? event.deltaY : event.deltaX) / this.viewport.zoom,
        (horizontal ? 0 : event.deltaY) / this.viewport.zoom
      );
    }
  };

  private readonly _onMapKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (this.viewport.locked) return;
    const offsets: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const offset = offsets[event.key];
    if (offset) {
      event.preventDefault();
      this.viewport.applyDeltaCenter(
        (offset[0] * this.viewport.width) / this.viewport.zoom / 10,
        (offset[1] * this.viewport.height) / this.viewport.zoom / 10
      );
    } else if (event.key === '+' || event.key === '=' || event.key === '-') {
      event.preventDefault();
      this.viewport.zoomByStep(event.key === '-' ? -1 : 1);
    }
  };

  private readonly _handleOpenMaximap = (event: Event) => {
    void this._openMaximap(event);
  };

  private readonly _handleToggleMaximap = (event: Event) => {
    if (this._maximapOpen) {
      event.stopPropagation();
      this._closeMaximap();
    } else {
      void this._openMaximap(event);
    }
  };

  private readonly _openMaximap = async (event: Event) => {
    event.stopPropagation();
    if (this.expanded || this._maximapOpen) return;
    this._maximapOpen = true;
    this._stopRemoteCursorLerp();
    await this.updateComplete;
    if (!this.isConnected || !this._maximapOpen || !this._dialog) return;
    const dialog = this._dialog as HTMLDialogElement;
    this._stopMaximapLayout = observeMaximapLayout(this.std, dialog, this.host);
    dialog.showModal();
    // The small map hides behind the overview, which starts as its picture in its place.
    this.toggleAttribute(MAXIMAP_OPEN_ATTRIBUTE, true);
    this.dispatchEvent(
      new CustomEvent('maximap-open-changed', { detail: true })
    );
    await this._maximap?.updateComplete;
    if (!dialog.open || dialog.hasAttribute('data-closing')) return;
    this._morphProgress = 0;
    void this._morphMaximap(1)?.finished.then(outcome => {
      if (outcome === 'finished') this._settleMaximap();
    });
  };

  /** Both ends of the overview's growth, measured now: this small map and the overview at rest. */
  private _maximapEnds(): [small: MaximapEnd, full: MaximapEnd] | null {
    const surface = this._maximapSurface;
    const map = this._maximap;
    if (!surface || !map) return null;
    const place = surface.getBoundingClientRect();
    const own = this.getBoundingClientRect();
    const small = this.restCamera();
    const full = map.restCamera();
    if (!small || !full || place.width <= 0 || own.width <= 0) return null;
    return [
      {
        box: {
          left: own.left - place.left,
          top: own.top - place.top,
          width: own.width,
          height: own.height,
        },
        radius: parseFloat(getComputedStyle(this).borderTopLeftRadius) || 0,
        camera: small,
      },
      {
        box: { left: 0, top: 0, width: place.width, height: place.height },
        radius: MAXIMAP_RADIUS,
        camera: full,
      },
    ];
  }

  /** Show the overview at `progress` of its growth: its container's box and the map in it. */
  private _showMaximapAt(ends: [MaximapEnd, MaximapEnd], progress: number) {
    const container = this._maximapFrame;
    if (!container) return;
    const frame = maximapMorphFrame(ends[0], ends[1], progress);
    this._morphProgress = progress;
    Object.assign(container.style, {
      left: `${frame.box.left}px`,
      top: `${frame.box.top}px`,
      width: `${frame.box.width}px`,
      height: `${frame.box.height}px`,
      borderRadius: `${frame.radius}px`,
    });
    container.style.setProperty('--maximap-detail', String(frame.detail));
    this._maximap?.paintMorph(frame);
  }

  /** Grow the overview out of the small map (1), or shrink it back into it (0), from where it is. */
  private _morphMaximap(to: 0 | 1) {
    const container = this._maximapFrame;
    const ends = this._maximapEnds();
    if (!container || !ends) return null;
    const from = this._morphProgress;
    this._showMaximapAt(ends, from);
    return streamLainAnimationController.animateProgress(
      container,
      progress => this._showMaximapAt(ends, from + (to - from) * progress),
      'morph'
    );
  }

  /** The overview is at rest: its container and map follow their place again. */
  private _settleMaximap() {
    this._morphProgress = 1;
    this._maximapFrame?.removeAttribute('style');
    this._maximap?.paintMorph(null);
  }

  private readonly _finishMaximapClose = () => {
    const dialog = this._dialog as HTMLDialogElement | null;
    if (dialog) handOverToSmallMap(this, dialog);
  };

  private readonly _closeMaximap = () => {
    const dialog = this._dialog as HTMLDialogElement | null;
    if (!dialog?.open || dialog.hasAttribute('data-closing')) return;
    // Keep the dialog and backdrop mounted until the map is back in its small place.
    dialog.setAttribute('data-closing', '');
    // The small map shows the canvas as it is now; the overview shrinks into exactly that picture.
    this._render();
    const morph = this._morphMaximap(0);
    if (!morph) {
      this._finishMaximapClose();
      return;
    }
    void morph.finished.then(this._finishMaximapClose);
  };

  private readonly _onMaximapClosed = () => {
    this._dialog?.removeAttribute('data-closing');
    this.toggleAttribute(MAXIMAP_OPEN_ATTRIBUTE, false);
    this._stopMaximapLayout?.();
    this._stopMaximapLayout = null;
    this._maximapOpen = false;
    this._morphProgress = 0;
    this.dispatchEvent(
      new CustomEvent('maximap-open-changed', { detail: false })
    );
    this._outsidePointerDown = false;
    this._scheduleRender();
    if (this.isConnected) this._updateRemoteCursorTargets();
  };

  private _isOutsideMap(event: MouseEvent) {
    if (event.target !== this._dialog || !this._maximapSurface) return false;
    const rect = this._maximapSurface.getBoundingClientRect();
    return (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    );
  }

  override render() {
    return html`
      <canvas
        tabindex="0"
        aria-label=${edgelessToolbarI18nT('toolbar.minimapLabel')}
        @keydown=${this._onMapKeyDown}
        @wheel=${this._onWheel}
        @pointerdown=${this._onPointerDown}
        @pointermove=${this._onPointerMove}
        @pointerup=${this._onPointerUp}
        @pointercancel=${this._onPointerUp}
        @lostpointercapture=${this._onPointerUp}
      ></canvas>
      ${this.expanded
        ? nothing
        : html`
            <dialog
              class="maximap-dialog"
              aria-label=${edgelessToolbarI18nT('toolbar.canvasOverview')}
              @close=${this._onMaximapClosed}
              @cancel=${(event: Event) => {
                event.preventDefault();
                event.stopPropagation();
                this._closeMaximap();
              }}
              @keydown=${(event: Event) => event.stopPropagation()}
              @wheel=${(event: Event) => {
                event.stopPropagation();
                event.preventDefault();
              }}
              @pointerdown=${(event: PointerEvent) => {
                this._outsidePointerDown = this._isOutsideMap(event);
                event.stopPropagation();
              }}
              @click=${(event: MouseEvent) => {
                event.stopPropagation();
                if (this._outsidePointerDown && this._isOutsideMap(event))
                  this._closeMaximap();
                this._outsidePointerDown = false;
              }}
            >
              ${this._maximapOpen
                ? html`
                    <div class="maximap-surface">
                      <div class="maximap-frame">
                        <edgeless-minimap-canvas
                          .std=${this.std}
                          .host=${this.host}
                          expanded
                        ></edgeless-minimap-canvas>
                        <button
                          type="button"
                          class="map-control"
                          autofocus
                          aria-label=${edgelessToolbarI18nT(
                            'toolbar.closeCanvasOverview'
                          )}
                          title=${edgelessToolbarI18nT(
                            'toolbar.closeCanvasOverviewHint'
                          )}
                          @click=${this._closeMaximap}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.8"
                            stroke-linecap="round"
                          >
                            <path d="m6 6 12 12M18 6 6 18" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  `
                : nothing}
            </dialog>
          `}
    `;
  }
}
